// SKILL-OVERRIDES (founder 2026-10-30: "agent will give it if asked anyway, so make it available,
// always with a default button"): a skill in Team › Skills opens in a modal —
//   - Default: our text, read-only;
//   - Company additions: the company's own lines on top (managers edit; versioned; members read);
//   - "Differs from default": the additions as added lines, and when we shipped a newer default
//     ("Default updated"), what changed in it — the additions stay until a manager reviews;
//   - Reset to default (with a confirm).
// A self-hosted skill (its text lives in the client's own runtime) is view only, with "Request a
// change" (filed to the support log; never to a named agent).

import type { OperationOutput } from "@companyos/sdk";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  InfoTip,
  Notice,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  toast,
} from "@companyos/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RotateCcwIcon, SendIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { formatDate } from "../format";
import { problemText } from "../ops-parts";
import { LoadError, Loading } from "../screen-parts";
import { type DiffLine, diffCounts, lineDiff } from "./skill-diff";

export type SkillState = OperationOutput<"skills.list">["data"][number];
type Detail = OperationOutput<"skills.get">;

const skillsKey = (co: string) => ["company", co, "skills"] as const;
const skillKey = (co: string, key: string) => ["company", co, "skills", key] as const;

/** Each skill with our default or additions (the library's badges). */
export const skillStatesQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: skillsKey(co),
    queryFn: () => api.client.skills.list({ params: { co } }),
  });

const skillQuery = (api: Api, co: string, key: string) =>
  queryOptions({
    queryKey: skillKey(co, key),
    queryFn: () => api.client.skills.get({ params: { co, key } }),
  });

const LAYER_INFO =
  "Our default stays the base. Your additions apply alongside it; where one conflicts with it, yours wins. The DE gets them on its next run.";

/** The library row's badges: customized, a new default to review, or self-hosted. */
export function SkillStateBadges({ state }: { state: SkillState | undefined }) {
  if (!state) return <Badge variant="neutral">Self-hosted</Badge>;
  return (
    <>
      {state.default_updated ? (
        <Badge variant="warning" data-slot="skill-default-updated">
          Default updated
        </Badge>
      ) : null}
      {state.has_additions ? (
        <Badge variant="info" data-slot="skill-customized">
          Differs from default
        </Badge>
      ) : null}
    </>
  );
}

const Pre = ({ text, slot }: { text: string; slot?: string }) => (
  <pre
    data-slot={slot}
    className="max-h-[46vh] overflow-y-auto rounded-md border border-subtle bg-sunken p-3 font-mono text-xs whitespace-pre-wrap text-foreground [scrollbar-width:thin]"
  >
    {text}
  </pre>
);

const Diff = ({ lines, slot }: { lines: readonly DiffLine[]; slot: string }) => (
  <div
    data-slot={slot}
    className="max-h-[40vh] overflow-y-auto rounded-md border border-subtle font-mono text-xs [scrollbar-width:thin]"
  >
    {lines.map((l, i) => (
      <div
        // biome-ignore lint/suspicious/noArrayIndexKey: a diff's lines repeat; the order is the key
        key={i}
        data-diff={l.kind}
        className={`px-3 whitespace-pre-wrap ${l.kind === "add" ? "bg-success-soft text-success-text" : l.kind === "del" ? "bg-danger-soft text-danger-text line-through" : "text-muted-foreground"}`}
      >
        <span aria-hidden="true" className="mr-2 select-none">
          {l.kind === "add" ? "+" : l.kind === "del" ? "−" : " "}
        </span>
        {l.text || " "}
      </div>
    ))}
  </div>
);

export function SkillDialog({
  api,
  company,
  skill,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  /** The skill to show; null: closed. */
  skill: string | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={skill !== null} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="sm:max-w-3xl" data-slot="skill-dialog">
        {skill ? <SkillBody api={api} company={company} skill={skill} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function SkillBody({ api, company, skill }: { api: Api; company: CompanyAccess; skill: string }) {
  const q = useQuery(skillQuery(api, company.id, skill));
  if (q.isError)
    return (
      <>
        <DialogHeader>
          <DialogTitle className="font-mono">{skill}</DialogTitle>
        </DialogHeader>
        <LoadError what="The skill" error={q.error} />
      </>
    );
  if (!q.data)
    return (
      <>
        <DialogHeader>
          <DialogTitle className="font-mono">{skill}</DialogTitle>
        </DialogHeader>
        <Loading label="Loading the skill" rows={3} />
      </>
    );
  const d = q.data;
  return d.hosting === "self_hosted" ? (
    <SelfHosted api={api} company={company} d={d} />
  ) : (
    <Ours api={api} company={company} d={d} />
  );
}

function SelfHosted({ api, company, d }: { api: Api; company: CompanyAccess; d: Detail }) {
  const [asking, setAsking] = useState(false);
  const [what, setWhat] = useState("");
  const send = useMutation({
    mutationFn: () =>
      api.client.skills.changeRequest({
        params: { co: company.id, key: d.key },
        body: { what: what.trim() },
      }),
    onSuccess: () => {
      toast.add({ title: "Change requested", type: "success" });
      setAsking(false);
      setWhat("");
    },
    onError: (e) => toast.add({ title: problemText(e, "Not sent."), type: "error" }),
  });
  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <span className="font-mono">{d.key}</span>
          <Badge variant="neutral">Self-hosted</Badge>
          <Badge variant="outline">View only</Badge>
        </DialogTitle>
        <DialogDescription className="flex items-center gap-1">
          Runs in your own runtime.
          <InfoTip label="About self-hosted skills">
            This skill's text lives in your self-hosted runtime, not in CompanyOS. Ask for a change
            and our team makes it there.
          </InfoTip>
        </DialogDescription>
      </DialogHeader>
      {d.can_edit ? (
        asking ? (
          <form
            className="flex flex-col gap-2"
            data-slot="skill-change-request"
            onSubmit={(e) => {
              e.preventDefault();
              if (what.trim()) send.mutate();
            }}
          >
            <label htmlFor="skill-change-what" className="text-sm font-medium">
              What should change?
            </label>
            <Textarea
              id="skill-change-what"
              value={what}
              maxLength={400}
              rows={4}
              onChange={(e) => setWhat(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setAsking(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!what.trim() || send.isPending}>
                <SendIcon aria-hidden="true" />
                Send request
              </Button>
            </div>
          </form>
        ) : (
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setAsking(true)}>
              Request a change
            </Button>
          </div>
        )
      ) : null}
    </>
  );
}

function Ours({ api, company, d }: { api: Api; company: CompanyAccess; d: Detail }) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<string>(d.has_additions ? "additions" : "default");
  const [draft, setDraft] = useState(d.additions ?? "");
  const [confirmReset, setConfirmReset] = useState(false);
  // The version the draft was taken from: a save is checked against it (409 when another edit or
  // a newer default landed since), and a refetch never overwrites lines not yet saved.
  const [from, setFrom] = useState({
    n: d.n,
    text: d.additions ?? "",
    hash: d.default?.content_hash,
  });
  const unsaved = draft.trim() !== from.text;
  // biome-ignore lint/correctness/useExhaustiveDependencies: only a new server version syncs
  useEffect(() => {
    if (unsaved) return;
    setDraft(d.additions ?? "");
    setFrom({ n: d.n, text: d.additions ?? "", hash: d.default?.content_hash });
  }, [d.n, d.additions, d.default?.content_hash]);
  const save = useMutation({
    mutationFn: (v: { text: string | null; n: number; hash: string | undefined }) =>
      api.client.skills.additions.put({
        params: { co: company.id, key: d.key },
        body: { text: v.text, expected_n: v.n, ...(v.hash ? { default_hash: v.hash } : {}) },
      }),
    onSuccess: async (next, v) => {
      setDraft(next.additions ?? "");
      setFrom({ n: next.n, text: next.additions ?? "", hash: next.default?.content_hash });
      qc.setQueryData(skillKey(company.id, d.key), next);
      await qc.invalidateQueries({ queryKey: skillsKey(company.id), exact: true });
      toast.add({
        title: v.text === null ? "Reset to default" : next.n === v.n ? "No change" : "Saved",
        type: "success",
      });
    },
    onError: async (e) => {
      toast.add({ title: problemText(e, "Not saved."), type: "error" });
      // A conflict: show the newest version; the person's lines stay, and saving again
      // replaces it knowingly.
      const fresh = await qc
        .fetchQuery({ ...skillQuery(api, company.id, d.key), staleTime: 0 })
        .catch(() => null);
      if (fresh)
        setFrom({ n: fresh.n, text: fresh.additions ?? "", hash: fresh.default?.content_hash });
    },
  });
  const added = useMemo(() => lineDiff("", d.additions), [d.additions]);
  const changed = useMemo(
    () => (d.default_updated ? lineDiff(d.previous_default_text, d.default?.text) : []),
    [d.default_updated, d.previous_default_text, d.default?.text],
  );
  const counts = diffCounts(changed);
  const dirty = draft.trim() !== (d.additions ?? "");
  // Mark reviewed and reset act on what is shown now.
  const shown = { n: d.n, hash: d.default?.content_hash };
  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex flex-wrap items-center gap-2">
          <span className="font-mono">{d.key}</span>
          {d.default ? <Badge variant="outline">Default {d.default.version}</Badge> : null}
          {d.has_additions ? <Badge variant="info">Differs from default</Badge> : null}
          {d.default_updated ? <Badge variant="warning">Default updated</Badge> : null}
        </DialogTitle>
        <DialogDescription className="flex items-center gap-1">
          {d.default?.name}
          <InfoTip label="How additions work">{LAYER_INFO}</InfoTip>
        </DialogDescription>
      </DialogHeader>
      {d.default_updated ? (
        <Notice
          tone="warning"
          data-slot="skill-default-updated-notice"
          action={
            d.can_edit ? (
              <Button
                size="sm"
                variant="outline"
                disabled={save.isPending}
                onClick={() => save.mutate({ text: d.additions, ...shown })}
              >
                Mark reviewed
              </Button>
            ) : null
          }
        >
          We shipped default {d.default?.version}. Your additions stay.
        </Notice>
      ) : null}
      <Tabs value={tab} onValueChange={(v) => setTab(String(v))}>
        <TabsList variant="line" className="w-full">
          <TabsTrigger value="default">Default</TabsTrigger>
          <TabsTrigger value="additions">Company additions</TabsTrigger>
          {d.has_additions || d.default_updated ? (
            <TabsTrigger value="diff">Differences</TabsTrigger>
          ) : null}
          {d.versions.length ? <TabsTrigger value="versions">Versions</TabsTrigger> : null}
        </TabsList>
        <TabsContent value="default" className="pt-3">
          <Pre text={d.default?.text ?? ""} slot="skill-default-text" />
        </TabsContent>
        <TabsContent value="additions" className="flex flex-col gap-2 pt-3">
          {d.can_edit ? (
            <>
              <Textarea
                aria-label="Company additions"
                data-slot="skill-additions-input"
                value={draft}
                rows={8}
                maxLength={20_000}
                placeholder="Lines to add on top of our default."
                onChange={(e) => setDraft(e.target.value)}
                className="font-mono text-xs"
              />
              <div className="flex items-center gap-2">
                {d.has_additions ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={save.isPending}
                    onClick={() => setConfirmReset(true)}
                    data-slot="skill-reset"
                  >
                    <RotateCcwIcon aria-hidden="true" />
                    Reset to default
                  </Button>
                ) : null}
                <Button
                  className="ml-auto"
                  size="sm"
                  disabled={!dirty || save.isPending}
                  onClick={() =>
                    save.mutate({ text: draft.trim() ? draft : null, n: from.n, hash: from.hash })
                  }
                  data-slot="skill-save"
                >
                  Save
                </Button>
              </div>
            </>
          ) : d.additions ? (
            <Pre text={d.additions} slot="skill-additions-text" />
          ) : (
            <p className="text-sm text-muted-foreground">None. The DE follows our default.</p>
          )}
        </TabsContent>
        <TabsContent value="diff" className="flex flex-col gap-3 pt-3">
          {d.has_additions ? (
            <section className="flex flex-col gap-1" aria-label="Your additions">
              <h3 className="text-xs font-medium text-muted-foreground uppercase">
                Added on top of default {d.base_version ?? d.default?.version}
              </h3>
              <Diff lines={added} slot="skill-diff-additions" />
            </section>
          ) : null}
          {d.default_updated ? (
            <section className="flex flex-col gap-1" aria-label="What changed in the default">
              <h3 className="text-xs font-medium text-muted-foreground uppercase">
                Default {d.base_version} → {d.default?.version} · +{counts.added} −{counts.removed}
              </h3>
              {d.previous_default_text !== null ? (
                <Diff lines={changed} slot="skill-diff-default" />
              ) : (
                <p className="text-sm text-muted-foreground">The earlier default is gone.</p>
              )}
            </section>
          ) : null}
        </TabsContent>
        <TabsContent value="versions" className="pt-3">
          <ol className="m-0 flex list-none flex-col gap-1 p-0" data-slot="skill-versions">
            {d.versions.map((v) => (
              <li key={v.n} className="text-sm" data-version={v.n}>
                <details className="group/ver">
                  <summary
                    className={`flex items-center gap-2 rounded-sm py-0.5 ${v.text ? "cursor-pointer hover:bg-sunken" : "list-none"}`}
                  >
                    <span className="w-8 font-mono text-xs text-muted-foreground">v{v.n}</span>
                    <span>
                      {v.via === "reset"
                        ? "Reset to default"
                        : v.via === "review"
                          ? "Reviewed new default"
                          : "Edited"}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      on default {v.base_version}
                    </span>
                    <span className="ml-auto text-xs text-muted-foreground">
                      {formatDate(v.created_at)}
                    </span>
                  </summary>
                  {v.text ? <Pre text={v.text} /> : null}
                </details>
              </li>
            ))}
          </ol>
        </TabsContent>
      </Tabs>
      <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
        <AlertDialogContent size="sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Reset to default?</AlertDialogTitle>
            <AlertDialogDescription>
              Your additions are removed (kept in Versions). The DE gets our default on its next
              run.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmReset(false);
                save.mutate({ text: null, ...shown });
              }}
            >
              Reset
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
